import { Cart } from '../types';
import { cartService } from './cartService';

/**
 * CartEventListener - Handles cart synchronization across browser tabs
 */
export class CartEventListener {
  private static instance: CartEventListener;
  private listeners: Set<(cart: Cart | null) => void> = new Set();
  private readonly STORAGE_KEY = 'the-frosting-room-cart';

  private constructor() {
    this.setupStorageListener();
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): CartEventListener {
    if (!CartEventListener.instance) {
      CartEventListener.instance = new CartEventListener();
    }
    return CartEventListener.instance;
  }

  /**
   * Add listener for cart changes
   */
  public addListener(callback: (cart: Cart | null) => void): void {
    this.listeners.add(callback);
  }

  /**
   * Remove listener
   */
  public removeListener(callback: (cart: Cart | null) => void): void {
    this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of cart changes
   */
  private notifyListeners(cart: Cart | null): void {
    this.listeners.forEach(callback => {
      try {
        callback(cart);
      } catch (error) {
        console.error('Error in cart listener:', error);
      }
    });
  }

  /**
   * Manually trigger cart update notification
   */
  public triggerUpdate(): void {
    try {
      const cart = cartService.loadCart();
      this.notifyListeners(cart);
    } catch (error) {
      console.error('Error triggering cart update:', error);
    }
  }

  /**
   * Setup storage event listener for multi-tab sync
   */
  private setupStorageListener(): void {
    window.addEventListener('storage', (event) => {
      if (event.key === this.STORAGE_KEY) {
        try {
          if (event.newValue === null) {
            // Cart was cleared
            this.notifyListeners(null);
          } else if (event.newValue) {
            // Cart was updated
            const cartData = JSON.parse(event.newValue);
            const cart: Cart = {
              ...cartData,
              lastUpdated: new Date(cartData.lastUpdated),
              items: cartData.items.map((item: any) => ({
                ...item,
                addedAt: new Date(item.addedAt),
                updatedAt: new Date(item.updatedAt),
                deliveryDate: item.deliveryDate ? new Date(item.deliveryDate) : undefined
              }))
            };
            this.notifyListeners(cart);
          }
        } catch (error) {
          console.error('Error processing storage event:', error);
        }
      }
    });
  }

  /**
   * Cleanup listeners
   */
  public cleanup(): void {
    this.listeners.clear();
  }
}

// Export singleton instance
export const cartEventListener = CartEventListener.getInstance();
